import type { ErrorEvent, EventHint } from '@sentry/react';
import { useSessionStore } from '@src/stores/use-session-store';
import { isRedirect } from '@tanstack/react-router';
import { isAxiosError } from 'axios';

const SESSION_REDIRECT_STATUS = 307;
const SESSION_REDIRECT_ROUTE = '/session-expired';
const RESPONSE_OBJECT_MESSAGE = '[object Response]';

const isAuth401 = (error: unknown): boolean =>
  (isAxiosError(error) && error.response?.status === 401) ||
  (error instanceof Response && error.status === 401);

// Network errors during a dead session are expected (the user's auth expired) — not actionable in Sentry.
const isAxiosNetworkError = (error: unknown): boolean =>
  isAxiosError(error) &&
  error.code === 'ERR_NETWORK' &&
  useSessionStore.getState().sessionStatus === 'dead';

// Checks all event surfaces where a TanStack Router redirect can appear:
// directly on the exception hint, the event message field, or in exception values.
const hasResponseObjectMarker = (error: unknown, event: ErrorEvent): boolean =>
  (error instanceof Error && error.message === RESPONSE_OBJECT_MESSAGE) ||
  (typeof event.message === 'string' &&
    event.message.includes(RESPONSE_OBJECT_MESSAGE)) ||
  (event.exception?.values?.some((v) =>
    v.value?.includes(RESPONSE_OBJECT_MESSAGE),
  ) ??
    false);

const hasSessionRedirectShape = (event: ErrorEvent): boolean => {
  const options = event.extra?.options;
  if (!options || typeof options !== 'object') {
    return false;
  }
  const { statusCode, to } = options as Record<string, unknown>;
  return (
    statusCode === SESSION_REDIRECT_STATUS && to === SESSION_REDIRECT_ROUTE
  );
};

export function silenceAuthErrors(
  event: ErrorEvent,
  hint: EventHint,
): ErrorEvent | null {
  const error = hint.originalException;

  if (isAuth401(error) || isAxiosNetworkError(error)) {
    return null;
  }

  // TanStack Router's redirect() extends Response — these are control flow, not errors.
  if (
    isRedirect(error) ||
    hasResponseObjectMarker(error, event) ||
    hasSessionRedirectShape(event)
  ) {
    return null;
  }

  return event;
}
